import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateStudentProfile, selectProfile } from '../redux/profileSlice';
import { supabase } from '../services/supabaseClient/supabaseClient';
import { updateUserProfile } from '../services/supabaseClient/profileService';
import FancyButton from '../components/ui/fancyButton';
import FormField from '../components/ui/FormField';
import TextInput from '../components/ui/TextInput';
import TextArea from '../components/ui/TextArea';
import Select from '../components/ui/Select';
import './StudentProfileForm.css';

const StudentProfileForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const existingProfile = useSelector(selectProfile);

  const [formData, setFormData] = useState(
    existingProfile || {
      name: '',
      age: '',
      educationLevel: '',
      interests: '',
      careerGoals: '',
      preferredLearningStyle: '',
      certifications: '',
      languagesSpoken: '',
    }
  );

  const [error] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      name,
      age,
      educationLevel,
      interests,
      careerGoals,
      preferredLearningStyle,
      certifications,
      languagesSpoken,
    } = formData;

    const profilePayload = {
      name,
      age: parseInt(age),
      education_level: educationLevel,
      interests:
        typeof interests === 'string'
          ? interests
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : interests || [],
      career_goals: careerGoals,
      preferred_learning_style: preferredLearningStyle,
      certifications:
        typeof certifications === 'string'
          ? certifications
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : certifications || [],
      languages_spoken:
        typeof languagesSpoken === 'string'
          ? languagesSpoken
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : languagesSpoken || [],
    };

    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;

      if (!userId) {
        throw new Error('User is not authenticated.');
      }

      // const profileDataWithUserId = { id: userId, ...profilePayload };

      // ✅ Save to Supabase (via profileService)
      const { error } = await updateUserProfile(userId, profilePayload);
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('❌ Failed to save profile: ' + error.message);
        return;
      }

      // ✅ Save to localStorage
      localStorage.setItem('student_profile', JSON.stringify(profilePayload));

      // ✅ Optionally update Redux store
      dispatch(updateStudentProfile(profilePayload));

      onClose(); // Close modal
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('❌ Error submitting profile: ' + err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="ml-student-profile-form">
      {error && <div className="ml-form-error">{error}</div>}

      <FormField label="Name" htmlFor="name" required>
        <TextInput id="name" name="name" value={formData.name} onChange={handleChange} required />
      </FormField>

      <FormField label="Age" htmlFor="age" required>
        <TextInput
          id="age"
          type="number"
          name="age"
          value={formData.age}
          onChange={handleChange}
          min="13"
          max="120"
          required
        />
      </FormField>

      <FormField label="Education Level" htmlFor="educationLevel" required>
        <Select
          id="educationLevel"
          name="educationLevel"
          value={formData.educationLevel}
          onChange={handleChange}
          options={[
            { value: 'High School', label: 'High School' },
            { value: 'Undergraduate', label: 'Undergraduate' },
            { value: 'Graduate', label: 'Graduate' },
            { value: 'PhD', label: 'PhD' },
            { value: 'Other', label: 'Other' },
          ]}
          placeholder="Select your education level"
          required
        />
      </FormField>

      <FormField label="Interests" htmlFor="interests">
        <TextArea
          id="interests"
          name="interests"
          value={formData.interests}
          onChange={handleChange}
          placeholder="What are you interested in learning?"
          rows={3}
        />
      </FormField>

      <FormField label="Career Goals" htmlFor="careerGoals">
        <TextArea
          id="careerGoals"
          name="careerGoals"
          value={formData.careerGoals}
          onChange={handleChange}
          placeholder="What are your career aspirations?"
          rows={3}
        />
      </FormField>

      <FormField label="Preferred Learning Style" htmlFor="preferredLearningStyle" required>
        <Select
          id="preferredLearningStyle"
          name="preferredLearningStyle"
          value={formData.preferredLearningStyle}
          onChange={handleChange}
          options={[
            { value: 'Visual', label: 'Visual' },
            { value: 'Auditory', label: 'Auditory' },
            { value: 'Reading/Writing', label: 'Reading/Writing' },
            { value: 'Kinesthetic', label: 'Kinesthetic' },
          ]}
          placeholder="Select your learning style"
          required
        />
      </FormField>

      <FormField
        label="Certifications"
        htmlFor="certifications"
        helpText="Comma-separated e.g. AWS, GCP"
      >
        <TextInput
          id="certifications"
          name="certifications"
          value={formData.certifications}
          onChange={handleChange}
          placeholder="AWS, GCP, etc."
        />
      </FormField>

      <FormField
        label="Languages Spoken"
        htmlFor="languagesSpoken"
        helpText="Comma-separated e.g. English, Swahili"
      >
        <TextInput
          id="languagesSpoken"
          name="languagesSpoken"
          value={formData.languagesSpoken}
          onChange={handleChange}
          placeholder="English, Swahili, etc."
        />
      </FormField>

      <div className="ml-form-actions">
        <FancyButton variant="primary" type="submit">
          Save Profile
        </FancyButton>
      </div>
    </form>
  );
};

export default StudentProfileForm;
